/**
 * Jeff's movement engine.
 *
 * The decision is made first, cryptographically (`lib/random.ts`). This module
 * then plans a *route that happens to end at the bowl already chosen*. Nothing
 * here can alter the outcome — `planPath` takes the direction as an input and
 * treats it as fixed.
 *
 * The plan is a pure data structure and `poseAt` is a pure function of elapsed
 * time. The render loop only samples it, which means:
 *   - the animation is trivially testable without a DOM or a clock,
 *   - a dropped frame changes nothing about where Jeff ends up,
 *   - a tab suspended mid-walk resumes correctly instead of drifting.
 *
 * Coordinate space is the pen's floor, normalised:
 *   x: 0 = left wall,   1 = right wall
 *   y: 0 = far end (the gate, where Jeff starts), 1 = near end (the bowls)
 * Components map this onto screen space; the engine never sees pixels.
 */

import type { Direction } from '../../shared/types.ts';
import { type Rng, createSeededRng, rngInt, rngRange } from './random.ts';
import { bowlSideFor } from '../config/jeff.ts';

// ── Geometry ────────────────────────────────────────────────────────────────

export interface Point {
  x: number;
  y: number;
}

export const PEN: {
  start: Point;
  bowls: Record<'left' | 'right', Point>;
  bowlApproachOffsetY: number;
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
} = {
  /** Where Jeff is standing when the gate opens. */
  start: { x: 0.5, y: 0.045 },
  /** Bowl centres. The pen renderer reads these too, so they cannot drift apart. */
  bowls: {
    left: { x: 0.245, y: 0.8 },
    right: { x: 0.755, y: 0.8 },
  },
  /** Jeff stops just short of a bowl rather than standing in it. */
  bowlApproachOffsetY: -0.075,
  /**
   * Walkable region. Inset from the walls by roughly Jeff's own width, and
   * stopped short of the bowl line: the bowls render on a layer above Jeff so
   * they occlude him correctly, which only holds while he stays behind them.
   */
  bounds: { minX: 0.1, maxX: 0.9, minY: 0.04, maxY: 0.78 },
};

export type Behaviour = 'walk' | 'hesitate' | 'peck' | 'scan' | 'feint' | 'commit' | 'arrived';

export interface Waypoint {
  x: number;
  y: number;
  /** Time spent travelling from the previous waypoint to this one. */
  travelMs: number;
  /** Time spent standing here on arrival. */
  pauseMs: number;
  behaviour: Behaviour;
}

export interface JeffPath {
  /** The predetermined outcome. Present so consumers never have to infer it. */
  direction: Direction;
  waypoints: Waypoint[];
  /**
   * Realised duration: the exact sum of every waypoint's travel and pause.
   *
   * Derived rather than asserted, so it can never desynchronise from the
   * waypoint list. The planner pads slack to land it on the duration it drew,
   * and clamps every segment so it cannot overshoot it.
   */
  totalMs: number;
  /** Final resting point — always the chosen bowl's approach position. */
  target: { x: number; y: number };
  /** True when Jeff made a show of considering the other bowl first. */
  feinted: boolean;
  seed: number;
}

export interface JeffPose {
  x: number;
  y: number;
  /** Radians. 0 points towards the camera (increasing y); grows clockwise. */
  heading: number;
  behaviour: Behaviour;
  /** 0 → 1 across the whole path. */
  progress: number;
  /** Gait cycle phase, 0 → 1, advancing only while Jeff is actually moving. */
  stride: number;
  /** 0 = head up, 1 = beak on the floor. */
  peck: number;
  moving: boolean;
  done: boolean;
}

// ── Planning ────────────────────────────────────────────────────────────────

export interface PlanOptions {
  direction: Direction;
  seed: number;
  minMs: number;
  maxMs: number;
  commitMs: number;
  /** Absolute ceiling on the walk. Defaults to one minute. */
  hardCapMs?: number;
  /** Skip the wander entirely and walk straight there. */
  reducedMotion?: boolean;
  reducedMotionMs?: number;
  /** Probability of the "wrong bowl, then a change of mind" performance. */
  feintChance?: number;
}

/** Metres-per-second equivalent, in normalised units per second. */
const SPEED = { slow: 0.13, normal: 0.21, brisk: 0.3, commit: 0.34 };

export function planPath(opts: PlanOptions): JeffPath {
  const {
    direction,
    seed,
    minMs,
    maxMs,
    commitMs,
    hardCapMs = 60_000,
    reducedMotion = false,
    reducedMotionMs = 2400,
    feintChance = 0.18,
  } = opts;

  const rng = createSeededRng(seed);
  const side = bowlSideFor(direction);
  const bowl = PEN.bowls[side];
  const target = { x: bowl.x, y: bowl.y + PEN.bowlApproachOffsetY };

  if (reducedMotion) {
    return {
      direction,
      waypoints: [
        { ...PEN.start, travelMs: 0, pauseMs: 0, behaviour: 'walk' },
        { ...target, travelMs: reducedMotionMs, pauseMs: 0, behaviour: 'commit' },
      ],
      totalMs: reducedMotionMs,
      target,
      feinted: false,
      seed,
    };
  }

  // Clamped here rather than trusting the caller: this is the only place that
  // can guarantee the walk terminates within the advertised time.
  const ceiling = Math.min(maxMs, hardCapMs);
  const totalMs = Math.round(rngRange(rng, Math.min(minMs, ceiling), ceiling));
  const wanderBudget = Math.max(0, totalMs - commitMs);
  const feinted = rng() < feintChance;

  const waypoints: Waypoint[] = [
    {
      ...PEN.start,
      travelMs: 0,
      // Clamped so a very short configured range cannot overrun the budget
      // before Jeff has taken a single step.
      pauseMs: Math.min(Math.round(rngRange(rng, 180, 420)), wanderBudget),
      behaviour: 'scan',
    },
  ];
  let spent = waypoints[0]!.pauseMs;
  let cursor = { ...PEN.start };

  // The feint is staged late, once Jeff has already covered ground, so it reads
  // as a change of mind rather than aimlessness.
  const feintAt = feinted ? wanderBudget * rngRange(rng, 0.5, 0.68) : Infinity;
  let feintDone = false;

  while (spent < wanderBudget) {
    const remaining = wanderBudget - spent;
    // Below one short step there is nothing useful left to do; the slack pass
    // below turns the remainder into a pause.
    if (remaining < MIN_STEP_MS) break;

    if (!feintDone && spent >= feintAt && remaining > 1400) {
      const wrongSide = side === 'left' ? 'right' : 'left';
      const wrongBowl = PEN.bowls[wrongSide];
      const decoy = clampToBounds({
        x: wrongBowl.x + rngRange(rng, -0.05, 0.05),
        y: wrongBowl.y - rngRange(rng, 0.14, 0.2),
      });
      const pauseMs = Math.round(rngRange(rng, 500, 950));
      pushSegment(waypoints, cursor, decoy, {
        speed: SPEED.brisk,
        pauseMs,
        behaviour: 'feint',
        // The decoy is across the run, so at a short configured duration the
        // crossing alone can outlast what is left of the wander budget. The
        // slack pass below only ever pads, never trims, so an overshoot here
        // would leak straight into `totalMs`. Fit the crossing to the time
        // available instead of bailing out — a hurried feint still reads as a
        // change of mind; a missing one costs the performance.
        maxTravelMs: remaining - pauseMs,
      });
      cursor = decoy;
      spent = sumDuration(waypoints);
      feintDone = true;
      continue;
    }

    const next = nextWanderPoint(rng, cursor, remaining);
    waypoints.push(next);
    cursor = { x: next.x, y: next.y };
    spent += next.travelMs + next.pauseMs;
  }

  const slack = wanderBudget - sumDuration(waypoints);
  if (slack > 0) {
    const last = waypoints[waypoints.length - 1]!;
    last.pauseMs += slack;
    if (last.behaviour === 'walk') last.behaviour = 'hesitate';
  }

  waypoints.push({ ...target, travelMs: commitMs, pauseMs: 0, behaviour: 'commit' });

  // Report what was actually built rather than what was intended. With the
  // slack padding above the two agree, but deriving it keeps `totalMs` and the
  // waypoint list impossible to desynchronise.
  return { direction, waypoints, totalMs: sumDuration(waypoints), target, feinted, seed };
}

interface SegmentOptions {
  speed: number;
  pauseMs: number;
  behaviour: Behaviour;
  /** Ceiling on travel time. Defaults to the usual longest single step. */
  maxTravelMs?: number;
}

function pushSegment(
  waypoints: Waypoint[],
  from: Point,
  to: Point,
  { speed, pauseMs, behaviour, maxTravelMs = MAX_STEP_MS }: SegmentOptions,
): void {
  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  // Floored at MIN_STEP_MS so a caller with almost no budget left gets one
  // honest short step rather than a zero-length teleport.
  const ceiling = Math.max(MIN_STEP_MS, Math.min(MAX_STEP_MS, maxTravelMs));
  const travelMs = clamp(Math.round((distance / speed) * 1000), MIN_STEP_MS, ceiling);
  waypoints.push({ x: to.x, y: to.y, travelMs, pauseMs, behaviour });
}

/** Shortest step worth planning. Below this, Jeff just stands there. */
const MIN_STEP_MS = 240;
const MAX_STEP_MS = 2800;

/**
 * Picks somewhere plausible for a chicken to go next.
 *
 * Real chickens drift laterally far more than they advance, and they advance in
 * short committed bursts. The bias here is deliberately sideways-heavy, with a
 * mild pull towards the near end so Jeff makes net progress without ever
 * appearing to head for a bowl.
 *
 * The step is guaranteed to fit inside `remainingMs`: rather than rejecting an
 * over-long move (which used to leave Jeff standing still for the last three
 * seconds of a short walk), the displacement is scaled down until it fits.
 */
function nextWanderPoint(rng: Rng, from: Point, remainingMs: number): Waypoint {
  const roll = rng();
  let behaviour: Behaviour;
  let speed: number;
  let pauseMs: number;

  if (roll < 0.26) {
    behaviour = 'peck';
    speed = SPEED.slow;
    pauseMs = rngInt(rng, 550, 1250);
  } else if (roll < 0.44) {
    behaviour = 'hesitate';
    speed = SPEED.slow;
    pauseMs = rngInt(rng, 420, 900);
  } else if (roll < 0.6) {
    behaviour = 'scan';
    speed = SPEED.normal;
    pauseMs = rngInt(rng, 300, 700);
  } else {
    behaviour = 'walk';
    speed = roll > 0.85 ? SPEED.brisk : SPEED.normal;
    pauseMs = rngInt(rng, 0, 320);
  }

  // Sideways bias: |dx| typically exceeds |dy|.
  let dx = rngRange(rng, -0.34, 0.34);
  let dy = rngRange(rng, -0.06, 0.17);

  // Budget for the movement itself, leaving the pause to take what is left.
  const travelBudget = clamp(remainingMs, MIN_STEP_MS, MAX_STEP_MS);
  const wanted = (Math.hypot(dx, dy) / speed) * 1000;
  if (wanted > travelBudget) {
    const shrink = travelBudget / wanted;
    dx *= shrink;
    dy *= shrink;
  }

  const candidate = clampToBounds({ x: from.x + dx, y: from.y + dy });
  const distance = Math.hypot(candidate.x - from.x, candidate.y - from.y);
  const travelMs = clamp(Math.round((distance / speed) * 1000), MIN_STEP_MS, travelBudget);

  // Trim the trailing pause rather than the movement when near the budget.
  return {
    ...candidate,
    travelMs,
    pauseMs: Math.min(pauseMs, Math.max(0, remainingMs - travelMs)),
    behaviour,
  };
}

function clampToBounds(p: Point): Point {
  return {
    x: clamp(p.x, PEN.bounds.minX, PEN.bounds.maxX),
    y: clamp(p.y, PEN.bounds.minY, PEN.bounds.maxY),
  };
}

function sumDuration(waypoints: Waypoint[]): number {
  return waypoints.reduce((total, w) => total + w.travelMs + w.pauseMs, 0);
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

// ── Sampling ────────────────────────────────────────────────────────────────

/**
 * Where Jeff is at `elapsedMs`.
 *
 * Pure, total, and monotonic in time. Called once per animation frame; does no
 * allocation beyond the returned pose and never reads from the DOM.
 */
export function poseAt(path: JeffPath, elapsedMs: number): JeffPose {
  const t = clamp(elapsedMs, 0, path.totalMs);
  const { waypoints } = path;

  // Handled before the walk so the final instant reads as "arrived" rather than
  // as the last frame of travel. Without this, `t === totalMs` matches the
  // commit segment's `t <= travelEnd` and reports `done: false` forever.
  if (t >= path.totalMs) return arrivedPose(path);

  let cursor = 0;
  let previous = waypoints[0]!;

  for (let i = 0; i < waypoints.length; i++) {
    const wp = waypoints[i]!;
    const travelEnd = cursor + wp.travelMs;
    const pauseEnd = travelEnd + wp.pauseMs;

    if (t <= travelEnd && wp.travelMs > 0) {
      const raw = (t - cursor) / wp.travelMs;
      const eased = wp.behaviour === 'commit' ? easeInOutSine(raw) : easeInOutQuad(raw);
      const x = lerp(previous.x, wp.x, eased);
      const y = lerp(previous.y, wp.y, eased);
      return {
        x,
        y,
        heading: headingBetween(previous, wp),
        behaviour: wp.behaviour,
        progress: t / path.totalMs,
        stride: strideAt(t),
        peck: 0,
        moving: true,
        done: false,
      };
    }

    if (t <= pauseEnd) {
      const pauseProgress = wp.pauseMs > 0 ? (t - travelEnd) / wp.pauseMs : 1;
      const next = waypoints[i + 1];
      // Turn towards the next objective during the back half of the pause: Jeff
      // stops, looks where he is going, then goes.
      const facing =
        next && pauseProgress > 0.5
          ? lerpAngle(
              headingBetween(previous, wp),
              headingBetween(wp, next),
              smoothstep((pauseProgress - 0.5) / 0.5),
            )
          : headingBetween(previous, wp);

      return {
        x: wp.x,
        y: wp.y,
        heading: facing,
        behaviour: wp.behaviour,
        progress: t / path.totalMs,
        stride: 0,
        peck: wp.behaviour === 'peck' ? peckCurve(t - travelEnd, wp.pauseMs) : 0,
        moving: false,
        done: false,
      };
    }

    cursor = pauseEnd;
    previous = wp;
  }

  // Unreachable: the durations sum to totalMs and t is clamped to it. Kept as
  // a total function rather than a throw, so a future planner bug degrades to a
  // parked chicken instead of a crashed animation loop.
  return arrivedPose(path);
}

/** Parked at the bowl, facing it. */
function arrivedPose(path: JeffPath): JeffPose {
  const last = path.waypoints[path.waypoints.length - 1]!;
  const bowl = PEN.bowls[bowlSideFor(path.direction)];
  return {
    x: last.x,
    y: last.y,
    heading: headingBetween(last, bowl),
    behaviour: 'arrived',
    progress: 1,
    stride: 0,
    peck: 0,
    moving: false,
    done: true,
  };
}

/** Heading in radians, with 0 = facing the camera (down-screen, +y). */
function headingBetween(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return 0;
  return Math.atan2(dx, dy);
}

/** Two steps per ~460 ms. Driven by absolute time so it never jumps at seams. */
function strideAt(elapsedMs: number): number {
  return (elapsedMs % 460) / 460;
}

/**
 * The shape of a peck.
 *
 * This used to be `sin²` on a fixed 520 ms cycle, which meant the head travelled
 * all the way up and all the way back down for every single dab — six full
 * head-lifts in a three-second pause. That is not what a chicken does. It drops
 * its head once, dabs at the litter several times *while already down*, and lifts
 * once at the end. The lift is the punctuation, not the beat.
 *
 * So: one descent, a run of shallow dabs around a held-down baseline, one lift.
 * Driven off the pause duration rather than a fixed period, so a long pause gets
 * more dabs rather than more lifts.
 *
 * Exported because the background flock runs its own state machine and would
 * otherwise grow a second, subtly different rhythm.
 */
const PECK_SHAPE = {
  /** Fraction of the pause spent lowering the head. */
  descend: 0.2,
  /** Fraction spent lifting it again. */
  lift: 0.16,
  /** Dabs per second while the head is down. */
  dabHz: 3.4,
  /** How far the head rises between dabs. Small — it never leaves the litter. */
  dabDepth: 0.16,
} as const;

export function peckCurve(pauseElapsedMs: number, pauseMs: number): number {
  if (pauseMs <= 0) return 0;
  const t = clamp(pauseElapsedMs / pauseMs, 0, 1);
  const { descend, lift, dabHz, dabDepth } = PECK_SHAPE;

  if (t < descend) return easeInOutSine(t / descend);
  if (t > 1 - lift) return easeInOutSine((1 - t) / lift);

  // Head is down. Shallow dabs about the floor, timed in real seconds so the
  // rate reads the same whatever the pause length.
  const downSeconds = (pauseElapsedMs - descend * pauseMs) / 1000;
  return 1 - dabDepth * Math.sin(downSeconds * Math.PI * dabHz) ** 2;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Interpolates the short way round the circle. */
function lerpAngle(a: number, b: number, t: number): number {
  let delta = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return a + delta * t;
}

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

function smoothstep(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}
